import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ASTERISK_API_BASE } from '../config/bridge';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const API_LIMIT = 500;
const PAGE_SIZE = 50;

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const text = (value) => String(value ?? '').trim();

const read = (source, keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return '';
};

const number = (source, keys) => {
  const value = Number(read(source, keys));
  return Number.isFinite(value) ? value : 0;
};

const normalizeDirection = (value) => {
  const normalized = text(value).toLowerCase();
  if (normalized.includes('out')) return 'Outbound';
  return 'Inbound';
};

const normalizeStatus = (value, direction, source = {}) => {
  const hangupReason = text(read(source, ['HangupReason', 'hangupReason', 'hangup_reason'])).toUpperCase();
  if (hangupReason === 'AGENT-REJECT') return 'Rejected';

  const normalized = text(value).toUpperCase().replace(/[\s-]+/g, '_');
  if (['ANSWER', 'ANSWERED', 'CONNECTED', 'COMPLETE', 'COMPLETED'].includes(normalized)) return 'Answered';
  if (normalized === 'REJECTED' || normalized === 'REJECT') return 'Rejected';
  if (normalized === 'VOICEMAIL') return 'Voicemail';
  if (normalized === 'BUSY') return 'Busy';
  if (['CANCEL', 'CANCELLED', 'CANCELED'].includes(normalized)) return 'Cancelled';
  if (['CHANUNAVAIL', 'UNAVAILABLE', 'CONGESTION'].includes(normalized)) return 'Unavailable';
  if (normalized === 'FAILED' || normalized === 'FAILURE') return 'Failed';
  if (['NOT_ANSWERED', 'NOANSWER', 'NO_ANSWER', 'ABANDONED', 'TIMEOUT'].includes(normalized)) {
    return direction === 'Inbound' ? 'Missed' : 'No Answer';
  }
  return normalized ? normalized.replace(/_/g, ' ') : 'Unknown';
};

const normalizeDate = (value) => {
  const raw = text(value);
  const parsed = raw ? new Date(raw) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

const formatDate = (date) => date
  ? date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '';

const formatTime = (date) => date
  ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
  : '';

const formatRangeDate = (value) => {
  const [year, month, day] = text(value).split('-').map(Number);
  if (!year || !month || !day) return text(value);
  return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainder = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

const normalizeOffer = (offer, index) => {
  const direction = 'Inbound';
  const eventAt = normalizeDate(read(offer, ['ResolvedAt', 'resolvedAt', 'resolved_at', 'OfferedAt', 'offeredAt', 'offered_at', 'UpdatedAt', 'updatedAt']));
  const rawStatus = read(offer, ['OfferStatus', 'offerStatus', 'offer_status']);

  return {
    key: `offer-${text(read(offer, ['CallOfferKey', 'callOfferKey']))}-${text(read(offer, ['OfferSequence', 'offerSequence']))}-${index}`,
    eventAt,
    agentExtension: text(read(offer, ['AgentExtension', 'agentExtension', 'agent_extension'])),
    agentName: text(read(offer, ['AgentName', 'agentName', 'agent_name'])),
    direction,
    callerNumber: text(read(offer, ['CallerNumber', 'callerNumber', 'caller_number'])),
    callerName: text(read(offer, ['CallerName', 'callerName', 'caller_name'])),
    status: normalizeStatus(rawStatus, direction, offer),
    ringSeconds: number(offer, ['RingTimeSeconds', 'ringTimeSeconds', 'ring_time_seconds']),
    talkSeconds: 0,
    totalSeconds: number(offer, ['RingTimeSeconds', 'ringTimeSeconds', 'ring_time_seconds']),
    queryId: text(read(offer, ['QueryId', 'queryId', 'query_id'])),
    uniqueId: text(read(offer, ['CallStateUniqueId', 'callStateUniqueId', 'UniqueId', 'uniqueId']))
  };
};

const normalizeCall = (call, index) => {
  const direction = normalizeDirection(read(call, ['Direction', 'direction']));
  const eventAt = normalizeDate(read(call, [
    'CallStartedAt', 'callStartedAt', 'call_started_at',
    'CreatedAt', 'createdAt', 'created_at',
    'CallEndedAt', 'callEndedAt', 'call_ended_at'
  ]));
  const rawStatus = read(call, ['Disposition', 'disposition', 'QueueStatus', 'queueStatus', 'queue_status']);
  const agentExtension = text(read(call, [
    'AgentExtension', 'agentExtension', 'agent_extension',
    'QueueAgent', 'queueAgent', 'queue_agent',
    'AnsweredBy', 'answeredBy', 'answered_by',
    'UserId', 'userId', 'user_id'
  ]));
  const uniqueId = text(read(call, ['UniqueId', 'uniqueId', 'unique_id', 'LinkedId', 'linkedId']));

  return {
    key: `call-${uniqueId || index}-${agentExtension}`,
    eventAt,
    agentExtension,
    agentName: text(read(call, ['AgentName', 'agentName', 'agent_name'])),
    direction,
    callerNumber: text(read(call, ['CallerNumber', 'callerNumber', 'caller_number'])),
    callerName: text(read(call, ['CallerName', 'callerName', 'caller_name'])),
    status: normalizeStatus(rawStatus, direction, call),
    ringSeconds: number(call, ['RingTime', 'ringTime', 'ring_time', 'RingTimeSeconds']),
    talkSeconds: number(call, ['OnCallTime', 'onCallTime', 'on_call_time', 'TalkSeconds', 'talkSeconds']),
    totalSeconds: number(call, ['TotalDuration', 'totalDuration', 'total_duration', 'DurationSeconds', 'durationSeconds']),
    queryId: text(read(call, ['QueryId', 'queryId', 'query_id'])),
    uniqueId
  };
};

const findArray = (payload, keys) => {
  const roots = [payload, payload?.data, payload?.result].filter(Boolean);
  for (const root of roots) {
    for (const key of keys) {
      if (Array.isArray(root?.[key])) return root[key];
    }
  }
  return [];
};

const buildRows = (payload) => {
  const offers = findArray(payload, ['queueOffers', 'queue_offers', 'offers']);
  const calls = findArray(payload, ['calls', 'callRows', 'call_rows']);
  const offerRows = offers.map(normalizeOffer);
  const callRows = calls.map(normalizeCall);
  const callsByUniqueId = new Map(
    callRows.filter((row) => row.uniqueId).map((row) => [row.uniqueId, row])
  );
  const enrichedOfferRows = offerRows.map((row) => {
    const matchingCall = callsByUniqueId.get(row.uniqueId);
    if (!matchingCall) return row;
    return {
      ...row,
      callerNumber: row.callerNumber || matchingCall.callerNumber,
      callerName: row.callerName || matchingCall.callerName,
      talkSeconds: matchingCall.talkSeconds,
      totalSeconds: matchingCall.totalSeconds || row.totalSeconds,
      queryId: row.queryId || matchingCall.queryId
    };
  });
  const relevantCalls = enrichedOfferRows.length
    ? callRows.filter((row) => row.direction === 'Outbound')
    : callRows;
  const uniqueRows = new Map();

  [...enrichedOfferRows, ...relevantCalls].forEach((row) => {
    const key = row.key || `${row.uniqueId}-${row.agentExtension}-${row.direction}`;
    if (!uniqueRows.has(key)) uniqueRows.set(key, row);
  });

  return Array.from(uniqueRows.values()).sort((left, right) =>
    (right.eventAt?.getTime() || 0) - (left.eventAt?.getTime() || 0)
  );
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const AgentCallHistoryReport = ({ ownToday = false, embedded = false, onClose }) => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState({
    startDate: todayIso(),
    endDate: todayIso(),
    agent: '',
    direction: '',
    status: '',
    callerNumber: ''
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceWarning, setSourceWarning] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(embedded ? 10 : PAGE_SIZE);

  const loadReport = useCallback(async () => {
    if (!filters.startDate || !filters.endDate) {
      setError('Start Date and End Date are required.');
      return;
    }
    if (filters.startDate > filters.endDate) {
      setError('Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSourceWarning('');
      const params = new URLSearchParams({
        from: filters.startDate,
        to: filters.endDate,
        limit: String(API_LIMIT)
      });
      const reportUrl = ownToday
        ? `${ASTERISK_API_BASE}/my-call-activity`
        : `${ASTERISK_API_BASE}/call-analytics?${params.toString()}`;
      const response = await fetch(reportUrl, {
        headers: authHeaders()
      });
      const responseText = await response.text();
      let payload = null;
      try {
        payload = responseText ? JSON.parse(responseText) : null;
      } catch {
        payload = null;
      }
      if (!response.ok) {
        throw new Error(payload?.error || payload?.message || responseText || `Request failed: ${response.status}`);
      }

      const nextRows = buildRows(payload || {});
      setRows(nextRows);
      setPage(1);
      if (nextRows.length >= API_LIMIT) {
        setSourceWarning(
          ownToday
            ? `Latest ${API_LIMIT} records are shown for today.`
            : `Latest ${API_LIMIT} records are shown. Use a shorter date range for a complete result.`
        );
      }
    } catch (err) {
      setRows([]);
      setPage(1);
      setError(err?.message || 'Failed to load Agent Call History.');
    } finally {
      setLoading(false);
    }
  }, [filters.endDate, filters.startDate, ownToday]);

  useEffect(() => {
    if (embedded) return undefined;
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, [embedded]);

  useEffect(() => {
    loadReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    const agentFilter = text(filters.agent).toLowerCase();
    const callerFilter = text(filters.callerNumber).toLowerCase();
    return rows.filter((row) => {
      const matchesAgent = !agentFilter ||
        row.agentExtension.toLowerCase().includes(agentFilter) ||
        row.agentName.toLowerCase().includes(agentFilter);
      const matchesCaller = !callerFilter ||
        row.callerNumber.toLowerCase().includes(callerFilter) ||
        row.callerName.toLowerCase().includes(callerFilter);
      const matchesDirection = !filters.direction || row.direction === filters.direction;
      const matchesStatus = !filters.status || row.status === filters.status;
      return matchesAgent && matchesCaller && matchesDirection && matchesStatus;
    });
  }, [filters.agent, filters.callerNumber, filters.direction, filters.status, rows]);

  const totals = useMemo(() => ({
    total: filteredRows.length,
    inbound: filteredRows.filter((row) => row.direction === 'Inbound').length,
    outbound: filteredRows.filter((row) => row.direction === 'Outbound').length,
    answered: filteredRows.filter((row) => row.status === 'Answered').length,
    missed: filteredRows.filter((row) => row.status === 'Missed' || row.status === 'No Answer').length,
    rejected: filteredRows.filter((row) => row.status === 'Rejected').length
  }), [filteredRows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const visibleRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);
  const firstVisibleRow = filteredRows.length ? ((page - 1) * pageSize) + 1 : 0;
  const lastVisibleRow = Math.min(page * pageSize, filteredRows.length);
  const firstPageButton = Math.max(1, Math.min(page - 1, totalPages - 2));
  const pageButtons = Array.from(
    { length: Math.min(3, totalPages) },
    (_, index) => firstPageButton + index
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({ ...previous, [name]: value.trimStart() }));
    setPage(1);
  };

  const exportExcel = () => {
    const exportRows = filteredRows.map((row) => ({
      Date: formatDate(row.eventAt),
      Time: formatTime(row.eventAt),
      'Agent Name': row.agentName,
      'Agent Extension': row.agentExtension,
      Direction: row.direction,
      'Caller Number': row.callerNumber,
      'Caller Name': row.callerName,
      Status: row.status,
      'Ring Time': formatDuration(row.ringSeconds),
      'Talk Time': formatDuration(row.talkSeconds),
      'Total Duration': formatDuration(row.totalSeconds),
      'Query ID': row.queryId,
      'Unique ID': row.uniqueId
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows), 'Call History');
    const exportName = ownToday
      ? `today-call-activity-${filters.startDate}.xlsx`
      : `agent-call-history-${filters.startDate}-${filters.endDate}.xlsx`;
    XLSX.writeFile(workbook, exportName);
  };

  const reportTitle = ownToday ? 'Today Call Activity' : 'Agent Call History';

  if (embedded) {
    return (
      <div className="today-activity-shell">
        <header className="today-activity-header">
          <div className="today-activity-title-group">
            <span className="today-activity-title-icon"><i className="bi bi-telephone-fill"></i></span>
            <div>
              <h2>{reportTitle}</h2>
              <p>Your inbound and outbound calls for today</p>
            </div>
          </div>
          <div className="today-activity-header-actions">
            <button type="button" className="today-activity-date" onClick={loadReport} disabled={loading} title="Refresh today's activity">
              <i className={`bi ${loading ? 'bi-arrow-repeat' : 'bi-calendar3'}`}></i>
              <span>{formatRangeDate(filters.startDate)} 00:00:00 - {formatRangeDate(filters.endDate)} 23:59:59</span>
              <i className="bi bi-arrow-clockwise"></i>
            </button>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
          </div>
        </header>

        {error ? <div className="today-activity-alert">{error}</div> : null}
        {sourceWarning ? <div className="today-activity-alert">{sourceWarning}</div> : null}

        <section className="today-activity-metrics" aria-label="Today call totals">
          <article className="today-activity-metric metric-total">
            <span className="today-activity-metric-icon"><i className="bi bi-telephone-fill"></i></span>
            <div><span>Total Calls</span><strong>{totals.total}</strong></div>
          </article>
          <article className="today-activity-metric metric-inbound">
            <span className="today-activity-metric-icon"><i className="bi bi-telephone-inbound-fill"></i></span>
            <div><span>Inbound</span><strong>{totals.inbound}</strong></div>
          </article>
          <article className="today-activity-metric metric-outbound">
            <span className="today-activity-metric-icon"><i className="bi bi-telephone-outbound-fill"></i></span>
            <div><span>Outbound</span><strong>{totals.outbound}</strong></div>
          </article>
          <article className="today-activity-metric metric-answered">
            <span className="today-activity-metric-icon"><i className="bi bi-check-circle"></i></span>
            <div><span>Answered</span><strong>{totals.answered}</strong></div>
          </article>
          <article className="today-activity-metric metric-missed">
            <span className="today-activity-metric-icon"><i className="bi bi-person-x-fill"></i></span>
            <div><span>Missed / No Answer</span><strong>{totals.missed}</strong></div>
          </article>
        </section>

        <section className="today-activity-details">
          <div className="today-activity-details-heading">
            <span><i className="bi bi-list-ul"></i></span>
            <h3>Call Details</h3>
          </div>
          <div className="today-activity-table-wrap">
            <table className="today-activity-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th><i className="bi bi-calendar3"></i> Date</th>
                  <th><i className="bi bi-clock"></i> Time</th>
                  <th><i className="bi bi-arrow-left-right"></i> Direction</th>
                  <th><i className="bi bi-telephone"></i> Caller Number</th>
                  <th>Status</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length ? visibleRows.map((row, index) => {
                  const statusClass = row.status.toLowerCase().replace(/[^a-z]+/g, '-');
                  return (
                    <tr key={row.key}>
                      <td>{firstVisibleRow + index}</td>
                      <td>{formatDate(row.eventAt)}</td>
                      <td>{formatTime(row.eventAt)}</td>
                      <td>
                        <span className={`today-activity-direction ${row.direction.toLowerCase()}`}>
                          <i className={`bi ${row.direction === 'Inbound' ? 'bi-arrow-down-left' : 'bi-arrow-up-right'}`}></i>
                          {row.direction}
                        </span>
                      </td>
                      <td>{row.callerNumber || '-'}</td>
                      <td><span className={`today-activity-status ${statusClass}`}>{row.status}</span></td>
                      <td>{formatDuration(row.talkSeconds || row.totalSeconds)}</td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="7" className="today-activity-empty">{loading ? 'Loading calls...' : 'No call activity found for today.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <footer className="today-activity-footer">
            <span>Showing {firstVisibleRow} to {lastVisibleRow} of {filteredRows.length} calls</span>
            <div className="today-activity-pagination">
              <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              {pageButtons.map((pageNumber) => (
                <button
                  type="button"
                  key={pageNumber}
                  className={pageNumber === page ? 'active' : ''}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              ))}
              <button type="button" aria-label="Next page" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
              <select
                aria-label="Calls per page"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value));
                  setPage(1);
                }}
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
              </select>
            </div>
          </footer>
        </section>
      </div>
    );
  }

  return (
    <div className="cdn-report-shell agent-state-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>{reportTitle}</h1>
          <p>{ownToday ? 'Your inbound and outbound calls for today' : 'Inbound and outbound call detail'}</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate(ownToday ? '/frontoffice-new' : '/backoffice')}>
            {ownToday ? 'Front Office' : 'Back Office'}
          </button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="daily-call-filter-grid">
          {!ownToday && <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>}
          {!ownToday && <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>}
          {!ownToday && <label><span>Agent</span><input type="text" name="agent" value={filters.agent} onChange={updateField} placeholder="Name or extension" /></label>}
          <label><span>Caller Number</span><input type="text" name="callerNumber" value={filters.callerNumber} onChange={updateField} placeholder="03..." /></label>
          <label>
            <span>Direction</span>
            <select name="direction" value={filters.direction} onChange={updateField}>
              <option value="">All</option>
              <option value="Inbound">Inbound</option>
              <option value="Outbound">Outbound</option>
            </select>
          </label>
          <label>
            <span>Status</span>
            <select name="status" value={filters.status} onChange={updateField}>
              <option value="">All</option>
              <option value="Answered">Answered</option>
              <option value="Missed">Missed</option>
              <option value="Rejected">Rejected</option>
              <option value="No Answer">No Answer</option>
              <option value="Busy">Busy</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Unavailable">Unavailable</option>
              <option value="Failed">Failed</option>
              <option value="Voicemail">Voicemail</option>
            </select>
          </label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={loadReport} disabled={loading}>{loading ? 'Loading' : 'Show'}</button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!filteredRows.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!filteredRows.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {sourceWarning ? <div className="cdn-alert">{sourceWarning}</div> : null}
      </section>

      <main className="cdn-report-paper agent-state-report-paper">
        <section className="cdn-report-heading">
          <h2>{reportTitle}</h2>
          <p><strong>Report Interval:</strong> {filters.startDate} 00:00:00 - {filters.endDate} 23:59:59</p>
          <p><strong>Agent / Direction / Status:</strong> {ownToday ? 'Logged-in agent' : filters.agent || 'All'} / {filters.direction || 'All'} / {filters.status || 'All'}</p>
        </section>

        <section className="daily-call-kpi-grid agent-state-kpi-grid">
          <article><span>Total Calls</span><strong>{totals.total}</strong></article>
          <article><span>Inbound</span><strong>{totals.inbound}</strong></article>
          <article><span>Outbound</span><strong>{totals.outbound}</strong></article>
          <article><span>Answered</span><strong>{totals.answered}</strong></article>
          <article><span>Missed / No Answer</span><strong>{totals.missed}</strong></article>
          <article><span>Rejected</span><strong>{totals.rejected}</strong></article>
        </section>

        <section className="daily-call-table-section">
          <h3 className="daily-call-section-title">Call Detail</h3>
          <table className="daily-call-report-table agent-state-detail-table">
            <thead>
              <tr>
                <th>Date</th><th>Time</th><th>Agent Name</th><th>Extension</th><th>Direction</th>
                <th>Caller Number</th><th>Caller Name</th><th>Status</th><th>Ring Time</th>
                <th>Talk Time</th><th>Total Duration</th><th>Query ID</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length ? visibleRows.map((row) => (
                <tr key={row.key}>
                  <td>{formatDate(row.eventAt)}</td>
                  <td>{formatTime(row.eventAt)}</td>
                  <td>{row.agentName || '-'}</td>
                  <td>{row.agentExtension || '-'}</td>
                  <td>{row.direction}</td>
                  <td>{row.callerNumber || '-'}</td>
                  <td>{row.callerName || '-'}</td>
                  <td>{row.status}</td>
                  <td>{formatDuration(row.ringSeconds)}</td>
                  <td>{formatDuration(row.talkSeconds)}</td>
                  <td>{formatDuration(row.totalSeconds)}</td>
                  <td>{row.queryId || '-'}</td>
                </tr>
              )) : (
                <tr><td colSpan="12" className="cdn-empty">{loading ? 'Loading calls...' : 'No call history found.'}</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      <div className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
        <span>Page {page} of {totalPages} ({filteredRows.length} records)</span>
        <button type="button" className="cdn-btn soft" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
      </div>
    </div>
  );
};

export default AgentCallHistoryReport;
